const toggleLabel = (button, labelElement, isPlaying) => {
    const action = isPlaying ? "Pause" : "Play";
    button.setAttribute("aria-pressed", String(isPlaying));
    button.setAttribute("aria-label", `${action} video`);
    if (labelElement) {
        labelElement.textContent = action;
    }
};

const YT_STATE_PLAYING = 1;
const YT_STATE_ENDED = 0;
const YT_STATE_PAUSED = 2;
const YT_STATE_CUED = 5;

const youtubePlayers = new Map();
let youtubeListenerRegistered = false;

const sendYoutubeMessage = (iframe, message) => {
    if (!iframe || !iframe.contentWindow) {
        return;
    }

    iframe.contentWindow.postMessage(JSON.stringify(message), "*");
};

const registerYoutubeListener = () => {
    if (youtubeListenerRegistered || typeof window === "undefined") {
        return;
    }

    window.addEventListener("message", (event) => {
        if (!event.data || !youtubePlayers.size) {
            return;
        }

        const player = youtubePlayers.get(event.source);
        if (!player) {
            return;
        }

        let data = event.data;
        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch {
                return;
            }
        }

        if (!data || typeof data !== "object") {
            return;
        }

        const updateFromState = (state) => {
            if (state === YT_STATE_PLAYING) {
                player.setPlaying(true);
            } else if (
                state === YT_STATE_ENDED ||
                state === YT_STATE_PAUSED ||
                state === YT_STATE_CUED
            ) {
                player.setPlaying(false);
            }
        };

        if (data.event === "onStateChange") {
            updateFromState(data.info);
        } else if (
            data.event === "infoDelivery" &&
            data.info &&
            Object.prototype.hasOwnProperty.call(data.info, "playerState")
        ) {
            updateFromState(data.info.playerState);
        }
    });

    youtubeListenerRegistered = true;
};

const ensureYoutubeParams = (iframe) => {
    if (!iframe) {
        return;
    }

    const src = iframe.getAttribute("src");
    if (!src) {
        return;
    }

    if (src.includes("enablejsapi=1")) {
        return;
    }

    const joinChar = src.includes("?") ? "&" : "?";
    iframe.setAttribute("src", `${src}${joinChar}enablejsapi=1`);
};

const setupYoutubeControls = (iframe, setPlaying) => {
    ensureYoutubeParams(iframe);
    registerYoutubeListener();

    const registerPlayer = () => {
        if (!iframe.contentWindow) {
            return;
        }

        youtubePlayers.set(iframe.contentWindow, { setPlaying });
        sendYoutubeMessage(iframe, { event: "listening", id: iframe.id || "hero-video-player" });
        sendYoutubeMessage(iframe, {
            event: "command",
            func: "addEventListener",
            args: ["onStateChange"],
        });
        sendYoutubeMessage(iframe, {
            event: "command",
            func: "addEventListener",
            args: ["infoDelivery"],
        });
    };

    if (iframe.contentWindow) {
        registerPlayer();
    }

    iframe.addEventListener("load", registerPlayer);

    return {
        play: () => {
            sendYoutubeMessage(iframe, { event: "command", func: "playVideo", args: [] });
            setPlaying(true);
        },
        pause: () => {
            sendYoutubeMessage(iframe, { event: "command", func: "pauseVideo", args: [] });
            setPlaying(false);
        },
        init: () => {
            setPlaying(false);
        },
    };
};

const attachHeroVideoControls = (wrapper) => {
    const media = wrapper.querySelector("[data-hero-video]");
    const toggle = wrapper.querySelector("[data-hero-video-toggle]");

    if (!media || !toggle) {
        return;
    }

    const label = toggle.querySelector("[data-hero-video-toggle-label]");
    let isPlaying = false;

    const setPlaying = (playing) => {
        isPlaying = Boolean(playing);
        toggleLabel(toggle, label, isPlaying);
    };

    let playMedia = () => {};
    let pauseMedia = () => {};

    if (media.tagName.toLowerCase() === "video") {
        const video = media;

        playMedia = () => {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(() => {
                    setPlaying(false);
                });
            }
        };

        pauseMedia = () => {
            video.pause();
        };

        const updateFromVideo = () => {
            setPlaying(!video.paused && !video.ended);
        };

        video.addEventListener("play", updateFromVideo);
        video.addEventListener("pause", updateFromVideo);
        video.addEventListener("ended", updateFromVideo);

        updateFromVideo();
    } else if (media.tagName.toLowerCase() === "iframe") {
        const controls = setupYoutubeControls(media, setPlaying);
        playMedia = controls.play;
        pauseMedia = controls.pause;
        controls.init();
    } else {
        setPlaying(false);
    }

    toggle.addEventListener("click", () => {
        if (isPlaying) {
            pauseMedia();
        } else {
            playMedia();
        }
    });
};

const initHeroVideoControls = () => {
    if (typeof document === "undefined") {
        return;
    }

    const wrappers = document.querySelectorAll("[data-hero-video-wrapper]");
    wrappers.forEach(attachHeroVideoControls);
};

initHeroVideoControls();
